import React from 'react'
import { useStyletron } from 'baseui'
import { lightTheme, superBrandGradient } from '@/customTheme'
import Tooltip from '@/components/Tooltip'

export type DefaultButtonSize = 'small' | 'standard' | 'large'
export type DefaultButtonType =
  /** Filled pink. */
  | 'primary'
  | 'primary-black'
  /** Backwards-compat alias for 'secondary-grey'. */
  | 'secondary'
  /** Filled grey, grey label. */
  | 'secondary-grey'
  /** Filled grey, black label. */
  | 'secondary-black'
  /** Premium pink brand gradient. */
  | 'primary-gradient'
  /** Yellow "Try Premium" CTA — create page header only. */
  | 'primary-orange'
  /** Super purple brand gradient. */
  | 'super-gradient'
  | 'whatnot'
  /** Transparent until hovered — low-emphasis pill action. */
  | 'minimal'
  /** Bare text link: no hit-box padding, medium weight, darkens on hover. */
  | 'text'
export type DefaultButtonIcon = 'iconLeft' | 'iconOnly' | 'noIcon'

export interface DefaultButtonProps {
  /**
   * Button label text.
   * Not required (and not rendered) when `icon='iconOnly'`.
   */
  label?: string
  /** Visual size of the button. Default: 'standard' */
  size?: DefaultButtonSize
  /** Visual style. Default: 'primary' */
  type?: DefaultButtonType
  /**
   * Icon layout:
   * - `'noIcon'`   — text only
   * - `'iconLeft'` — icon + text
   * - `'iconOnly'` — circular button showing only the icon, no text
   */
  icon?: DefaultButtonIcon
  /** Icon element. Required when `icon='iconLeft'` or `icon='iconOnly'`. Should be 24×24. */
  iconComponent?: React.ReactNode
  /** Disables pointer events and applies disabled color tokens */
  disabled?: boolean
  /** Stretches the button to fill its container's width. Has no effect when `icon='iconOnly'`. */
  fullWidth?: boolean
  /** Forces the active (pressed) visual state. Use when the button opens a persistent element like a popover. */
  isActive?: boolean
  /** Overrides the size preset's horizontal padding. */
  paddingHorizontal?: string
  /** Click handler */
  onClick?: () => void
  /** Accessible name when the visible label is omitted. */
  ariaLabel?: string
  /** Hover tooltip, primarily for icon-only buttons. */
  tooltip?: string
}

// ---------------------------------------------------------------------------
// Design tokens (sourced from lightTheme / customTheme)
// ---------------------------------------------------------------------------

const BORDER_RADIUS = '360px'
const ICON_SIZE = '24px'
const FONT_FAMILY = lightTheme.typography.Small14Semibold.fontFamily ?? 'New-Hero, sans-serif'

interface SizeConfig {
  height: string
  paddingVertical: string
  paddingHorizontal: string
  fontSize: string
  lineHeight: string
  letterSpacing: string
  iconGap: string
}

const SIZE_CONFIG: Record<DefaultButtonSize, SizeConfig> = {
  small: {
    height: '32px',
    paddingVertical: '4px',
    paddingHorizontal: '16px',
    fontSize: lightTheme.typography.Small14Semibold.fontSize,
    lineHeight: lightTheme.typography.Small14Semibold.lineHeight,
    letterSpacing: lightTheme.typography.Small14Semibold.letterSpacing ?? '-0.084px',
    iconGap: '6px',
  },
  standard: {
    height: '40px',
    paddingVertical: '8px',
    paddingHorizontal: '20px',
    fontSize: lightTheme.typography.Small14Semibold.fontSize,
    lineHeight: lightTheme.typography.Small14Semibold.lineHeight,
    letterSpacing: lightTheme.typography.Small14Semibold.letterSpacing ?? '-0.084px',
    iconGap: '6px',
  },
  large: {
    height: '48px',
    paddingVertical: '12px',
    paddingHorizontal: '24px',
    fontSize: lightTheme.typography.Small14Semibold.fontSize,
    lineHeight: lightTheme.typography.Small14Semibold.lineHeight,
    letterSpacing: lightTheme.typography.Small14Semibold.letterSpacing ?? '-0.084px',
    iconGap: '8px',
  },
}

interface ButtonColorSet {
  defaultBg: string
  hoverBg: string
  activeBg: string
  disabledBg: string
  defaultText: string
  disabledText: string
}

const PRIMARY_COLORS: ButtonColorSet = {
  defaultBg: lightTheme.colors.buttonPrimaryFill,
  hoverBg: lightTheme.colors.buttonPrimaryHover,
  activeBg: lightTheme.colors.buttonPrimaryActive,
  disabledBg: lightTheme.colors.grayScale50,
  defaultText: lightTheme.colors.white,
  disabledText: lightTheme.colors.grayScale200,
}

// 'secondary' is a backwards-compat alias for 'secondary-grey'.
const SECONDARY_GREY_COLORS: ButtonColorSet = {
  defaultBg: lightTheme.colors.buttonSecondaryFill,
  hoverBg: lightTheme.colors.buttonSecondaryHover,
  activeBg: lightTheme.colors.buttonSecondaryActive,
  disabledBg: lightTheme.colors.grayScale50,
  defaultText: lightTheme.colors.contentPrimary,
  disabledText: lightTheme.colors.grayScale200,
}

const SECONDARY_BLACK_COLORS: ButtonColorSet = {
  defaultBg: lightTheme.colors.buttonSecondaryFill,
  hoverBg: lightTheme.colors.buttonSecondaryHover,
  activeBg: lightTheme.colors.buttonSecondaryActive,
  disabledBg: lightTheme.colors.grayScale50,
  defaultText: lightTheme.colors.blackGray,
  disabledText: lightTheme.colors.grayScale200,
}

// Solid black fill — the long-standing "Apply all" / "Continue" CTA, previously
// only available as an inline style on the inspector footer button.
const PRIMARY_BLACK_COLORS: ButtonColorSet = {
  defaultBg: lightTheme.colors.blackGray,
  hoverBg: lightTheme.colors.grayScale800,
  activeBg: lightTheme.colors.grayScale700,
  disabledBg: lightTheme.colors.grayScale50,
  defaultText: lightTheme.colors.white,
  disabledText: lightTheme.colors.grayScale200,
}

// Transparent until interacted with — used for low-emphasis actions like header Reset.
const MINIMAL_COLORS: ButtonColorSet = {
  defaultBg: lightTheme.colors.buttonMinimalFill,
  hoverBg: lightTheme.colors.buttonMinimalHover,
  activeBg: lightTheme.colors.buttonMinimalActive,
  disabledBg: lightTheme.colors.buttonMinimalFill,
  defaultText: lightTheme.colors.blackGray,
  disabledText: lightTheme.colors.grayScale200,
}

// No fill in any state; hover darkens the label instead.
const TEXT_COLORS: ButtonColorSet = {
  defaultBg: 'transparent',
  hoverBg: 'transparent',
  activeBg: 'transparent',
  disabledBg: 'transparent',
  defaultText: lightTheme.colors.grayScale600,
  disabledText: lightTheme.colors.grayScale200,
}

// primary-gradient uses a CSS gradient background; ButtonColorSet holds the
// text and disabled tokens — the hover/active bg values are unused (overridden
// by gradient + box-shadow in the CSS).
const PRIMARY_GRADIENT_VALUE =
  'linear-gradient(120.015deg, rgb(255, 24, 102) 4.37%, rgb(253, 1, 103) 23.84%, rgb(240, 1, 136) 47.41%, rgb(221, 1, 160) 63.41%, rgb(210, 3, 180) 74.42%, rgb(200, 17, 203) 100%)'

const PRIMARY_GRADIENT_COLORS: ButtonColorSet = {
  defaultBg: PRIMARY_GRADIENT_VALUE,
  hoverBg: PRIMARY_GRADIENT_VALUE,
  activeBg: PRIMARY_GRADIENT_VALUE,
  disabledBg: lightTheme.colors.grayScale50,
  defaultText: lightTheme.colors.white,
  disabledText: lightTheme.colors.grayScale200,
}

const SUPER_GRADIENT_COLORS: ButtonColorSet = {
  defaultBg: superBrandGradient,
  hoverBg: superBrandGradient,
  activeBg: superBrandGradient,
  disabledBg: lightTheme.colors.grayScale50,
  defaultText: lightTheme.colors.white,
  disabledText: lightTheme.colors.grayScale200,
}

/** Glow colors per gradient family, used for the hover box-shadow. */
const GRADIENT_GLOW: Record<string, string> = {
  'primary-gradient': 'rgba(255, 5, 96, 0.5)',
  'super-gradient': 'rgba(117, 8, 255, 0.5)',
}

// Yellow "Try Premium" CTA — create page (dashboard) header only.
const PRIMARY_ORANGE_COLORS: ButtonColorSet = {
  defaultBg: lightTheme.colors.orange100,
  hoverBg: lightTheme.colors.orange200,
  activeBg: lightTheme.colors.orange300,
  disabledBg: lightTheme.colors.grayScale50,
  defaultText: lightTheme.colors.blackGray,
  disabledText: lightTheme.colors.grayScale200,
}

// Whatnot marketplace brand yellow — Batch Studio CSV export CTA only.
// Text/icon use Whatnot's own ink rather than our blackGray so the brand mark
// sits on an approved yellow + ink pairing.
const WHATNOT_COLORS: ButtonColorSet = {
  defaultBg: lightTheme.colors.whatnotYellow,
  hoverBg: lightTheme.colors.whatnotYellowHover,
  activeBg: lightTheme.colors.whatnotYellowActive,
  disabledBg: lightTheme.colors.grayScale50,
  defaultText: lightTheme.colors.whatnotInk,
  disabledText: lightTheme.colors.grayScale200,
}

// ---------------------------------------------------------------------------
// Style helpers
// ---------------------------------------------------------------------------

function getColors(type: DefaultButtonType): ButtonColorSet {
  if (type === 'primary') return PRIMARY_COLORS
  if (type === 'primary-gradient') return PRIMARY_GRADIENT_COLORS
  if (type === 'super-gradient') return SUPER_GRADIENT_COLORS
  if (type === 'primary-orange') return PRIMARY_ORANGE_COLORS
  if (type === 'whatnot') return WHATNOT_COLORS
  if (type === 'secondary-black') return SECONDARY_BLACK_COLORS
  if (type === 'primary-black') return PRIMARY_BLACK_COLORS
  if (type === 'minimal') return MINIMAL_COLORS
  if (type === 'text') return TEXT_COLORS
  return SECONDARY_GREY_COLORS
}

function isGradientType(type: DefaultButtonType): boolean {
  return type === 'primary-gradient' || type === 'super-gradient'
}

function getBackground(isGradient: boolean, disabled: boolean, colors: ButtonColorSet) {
  const bg = disabled ? colors.disabledBg : colors.defaultBg
  return isGradient && !disabled ? { background: bg } : { backgroundColor: bg }
}

function getHoverStyle(type: DefaultButtonType, isGradient: boolean, colors: ButtonColorSet) {
  if (isGradient) {
    return { boxShadow: `0px 0px 20px 0px ${GRADIENT_GLOW[type]}` }
  }
  // Bare text link has no fill to change — darken the label instead.
  if (type === 'text') {
    return { color: lightTheme.colors.blackGray }
  }
  return { backgroundColor: colors.hoverBg }
}

function getActiveStyle(isGradient: boolean, colors: ButtonColorSet) {
  return isGradient ? { boxShadow: 'none' } : { backgroundColor: colors.activeBg }
}

function getIconColor(disabled: boolean, colors: ButtonColorSet): string {
  return disabled ? colors.disabledText : colors.defaultText
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function DefaultButton({
  label,
  size = 'standard',
  type = 'primary',
  icon = 'noIcon',
  iconComponent,
  disabled = false,
  fullWidth = false,
  isActive = false,
  paddingHorizontal,
  onClick,
  ariaLabel,
  tooltip,
}: DefaultButtonProps) {
  const [css] = useStyletron()

  const sizeConf = SIZE_CONFIG[size]
  const paddingH = paddingHorizontal ?? sizeConf.paddingHorizontal
  const colors = getColors(type)
  const isGradient = isGradientType(type)
  const isIconOnly = icon === 'iconOnly'
  // A bare text link keeps only its line box: no size padding, no pill.
  const isTextLink = type === 'text'

  const textColor = disabled ? colors.disabledText : colors.defaultText

  const buttonClass = css({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: icon === 'iconLeft' ? sizeConf.iconGap : '0px',
    // Icon-only: square pill (same width as height), no text padding
    // fullWidth: stretch to container; ignored for icon-only
    width: isIconOnly ? sizeConf.height : fullWidth ? '100%' : undefined,
    height: isTextLink ? sizeConf.lineHeight : sizeConf.height,
    paddingTop: isIconOnly || isTextLink ? '0px' : sizeConf.paddingVertical,
    paddingBottom: isIconOnly || isTextLink ? '0px' : sizeConf.paddingVertical,
    paddingLeft: isIconOnly || isTextLink ? '0px' : paddingH,
    paddingRight: isIconOnly || isTextLink ? '0px' : paddingH,
    overflow: isIconOnly ? 'hidden' : undefined,
    borderRadius: isTextLink ? '0px' : BORDER_RADIUS,
    border: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    // Gradient type uses `background` (gradient string); solid types use `backgroundColor`.
    ...getBackground(isGradient, disabled, colors),
    ...(isActive && !disabled && getActiveStyle(isGradient, colors)),
    color: textColor,
    fontFamily: FONT_FAMILY,
    fontSize: sizeConf.fontSize,
    fontWeight: isTextLink ? '500' : '600',
    lineHeight: sizeConf.lineHeight,
    letterSpacing: sizeConf.letterSpacing,
    whiteSpace: 'nowrap',
    outline: 'none',
    flexShrink: '0',
    transitionProperty: isGradient ? 'box-shadow' : 'background-color',
    transitionDuration: '150ms',
    transitionTimingFunction: 'ease',
    ...(!disabled && {
      ':hover': isActive ? getActiveStyle(isGradient, colors) : getHoverStyle(type, isGradient, colors),
      ':active': getActiveStyle(isGradient, colors),
      ':focus-visible': {
        outline: `2px solid ${lightTheme.colors.primary}`,
        outlineOffset: '2px',
      },
    }),
  })

  const iconColor = getIconColor(disabled, colors)

  const iconWrapperClass = css({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: ICON_SIZE,
    height: ICON_SIZE,
    flexShrink: '0',
    color: iconColor,
  })

  const styledIcon =
    iconComponent != null && React.isValidElement(iconComponent)
      ? React.cloneElement(iconComponent as React.ReactElement<{ fill?: string }>, { fill: iconColor })
      : iconComponent

  const button = (
    <button
      className={buttonClass}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      type="button"
      aria-label={ariaLabel}
    >
      {(icon === 'iconLeft' || icon === 'iconOnly') && styledIcon != null && (
        <span className={iconWrapperClass}>{styledIcon}</span>
      )}
      {!isIconOnly && label}
    </button>
  )

  return tooltip ? <Tooltip info={{ content: tooltip }}>{button}</Tooltip> : button
}

export default DefaultButton
